import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { Chip, TablePagination } from '@mui/material'
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore'
import {
  collection,
  query,
  limit,
  startAfter,
  orderBy,
  where,
  getDoc,
  doc,
} from 'firebase/firestore'
import { firebase_firestore } from '../../../config/firebaseConfig'
import { useEffect, useRef } from 'react'
import { KeyboardArrowRight } from '@mui/icons-material'
import { useCollectionCount } from '../hooks/useCollectionCount'
import { sectionCard, tableHeadCell, tableBodyCell, adminColors } from '../../adminStyles'

const STATUS_CHIP_COLORS: Record<string, { bg: string; text: string }> = {
  member: { bg: 'rgba(27, 138, 42, 0.1)', text: adminColors.profitGreen },
  free: { bg: 'rgba(24, 187, 14, 0.1)', text: adminColors.accent },
  trial: { bg: 'rgba(184, 134, 11, 0.1)', text: adminColors.warningAmber },
  canceled: { bg: 'rgba(201, 55, 44, 0.08)', text: adminColors.lossRed },
  hold: { bg: 'rgba(140, 147, 155, 0.1)', text: adminColors.textMuted },
  starter: { bg: 'rgba(100, 100, 200, 0.1)', text: '#5555bb' },
}

function getStatusChipStyle(status: string) {
  const colors = STATUS_CHIP_COLORS[status] || {
    bg: 'rgba(140, 147, 155, 0.08)',
    text: adminColors.textMuted,
  }
  return { bgcolor: colors.bg, color: colors.text, borderColor: 'transparent' }
}

function getMostRecentStatusDate(tradingHouse: any): string | null {
  if (!tradingHouse?.statusHistory?.length) return null
  const last = tradingHouse.statusHistory[tradingHouse.statusHistory.length - 1]
  if (!last?.timestamp?.seconds) return null
  return new Date(last.timestamp.seconds * 1000).toLocaleDateString()
}

function TradingHouseCell({ tradingHouse }: { tradingHouse: any }) {
  if (!tradingHouse?.currentStatus?.status) {
    return <Typography sx={{ fontSize: '0.75rem', color: adminColors.textMuted }}>—</Typography>
  }

  const status = tradingHouse.currentStatus.status
  const date = getMostRecentStatusDate(tradingHouse)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
      <Chip
        label={status}
        size="small"
        sx={{
          ...getStatusChipStyle(status),
          fontSize: '0.7rem',
          fontWeight: 600,
          height: 20,
          textTransform: 'capitalize',
        }}
      />
      {date && (
        <Typography sx={{ fontSize: '0.675rem', color: adminColors.textMuted }}>{date}</Typography>
      )}
    </Box>
  )
}

async function getTraderSubscriptionDetails(traderUsermeta: any) {
  const subscription = (
    await getDoc(
      doc(firebase_firestore, `usermeta/${traderUsermeta.gcid}/subscriptions/greenchart`)
    )
  ).data()

  if (subscription) {
    const packagesOnSubscription = subscription.products.map((product: any) => product.name)
    const alertsPackage = subscription.products.find(
      (product: any) => product.productName === 'Alerts Package'
    )

    let trialPeriodEnding
    if (alertsPackage && 'promoDuration' in alertsPackage.metadata) {
      const packageCreated = new Date(alertsPackage.created * 1000)
      trialPeriodEnding = addDaysToDate(packageCreated, +alertsPackage.metadata.promoDuration)
    }

    traderUsermeta.subscription = {
      renewalDate: subscription.renewalDate,
      trialPeriod: trialPeriodEnding ? Math.floor(trialPeriodEnding.getTime() / 1000) : undefined,
      packages: packagesOnSubscription,
    }
  }

  function addDaysToDate(date: Date, days: number) {
    const newDate = new Date(date.valueOf())
    newDate.setDate(newDate.getDate() + days)
    return newDate
  }
}

function TraderRow(props: any) {
  const { traderUsermeta, traderUsermetaSelected } = props

  return (
    <TableRow
      sx={{
        cursor: 'pointer',
        transition: 'background-color 0.1s ease',
        '&:hover': { bgcolor: adminColors.accentHover },
      }}
      onClick={async () => {
        await getTraderSubscriptionDetails(traderUsermeta)
        traderUsermetaSelected(traderUsermeta)
      }}
    >
      <TableCell component="th" scope="row" sx={{ ...tableBodyCell, fontWeight: 500 }}>
        {traderUsermeta.email}
      </TableCell>
      <TableCell sx={tableBodyCell}>{traderUsermeta.firstName}</TableCell>
      <TableCell sx={tableBodyCell}>{traderUsermeta.lastName}</TableCell>
      <TableCell sx={tableBodyCell}>
        {(traderUsermeta.phoneNumber && traderUsermeta.phoneNumber.phoneNumber) ||
          (traderUsermeta.alertPhoneNumber && traderUsermeta.alertPhoneNumber.phoneNumber)}
      </TableCell>
      <TableCell sx={{ ...tableBodyCell, fontFamily: 'monospace', fontSize: '0.775rem' }}>
        {traderUsermeta.gcid}
      </TableCell>
      <TableCell sx={tableBodyCell}>
        <TradingHouseCell tradingHouse={traderUsermeta.tradingHouses?.greenchart} />
      </TableCell>
      <TableCell sx={tableBodyCell}>
        <TradingHouseCell tradingHouse={traderUsermeta.tradingHouses?.gtf} />
      </TableCell>
      <TableCell sx={{ ...tableBodyCell, width: 40, pr: 1.5 }}>
        <KeyboardArrowRight fontSize="small" sx={{ color: adminColors.textMuted }} />
      </TableCell>
    </TableRow>
  )
}

export default function TraderAccountTable(props: any) {
  const {
    searchString,
    searchField,
    gtfSearchSelected,
    greenchartSearchSelected,
    trader,
    traderUsermetaSelected,
    style,
  } = props
  const [page, setPage] = React.useState(0)
  const prevPageRef = useRef(0)

  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  const [usermetaQuery, setUsermetaQuery] = React.useState(
    query(collection(firebase_firestore, `usermeta`), orderBy('email'), limit(rowsPerPage))
  )

  const [nextTraders, tradersLoading, , tradersSnapshot, reloadCollectionData] =
    useCollectionDataOnce(usermetaQuery)
  const [tradersVisible, setTradersVisible] = React.useState<any>([])

  const allTraderUsermetaQuery = query(collection(firebase_firestore, `usermeta`), orderBy('email'))
  const traderUsermetaCount = useCollectionCount(allTraderUsermetaQuery)

  const [queriedTraders, setQueriedTrader] = React.useState<any>([])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    setQueriedTrader([])
    setPage(0)
    setUsermetaQuery(
      query(
        collection(firebase_firestore, `usermeta`),
        orderBy(searchField),
        limit(rowsPerPage),
        where(searchField, '>=', searchString)
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, gtfSearchSelected, greenchartSearchSelected])

  useEffect(() => {
    if (!nextTraders || tradersLoading) {
      return
    }

    setQueriedTrader(queriedTraders.concat(nextTraders))
    setTradersVisible(nextTraders)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextTraders])

  // No auto-select — user clicks a row to view details

  useEffect(() => {
    if (prevPageRef.current < page && queriedTraders.length <= page * rowsPerPage) {
      if (!tradersSnapshot || !tradersSnapshot.docs.length) {
        return
      }

      setUsermetaQuery(
        query(usermetaQuery, startAfter(tradersSnapshot.docs[tradersSnapshot.docs.length - 1]))
      )
    } else {
      setTradersVisible(queriedTraders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
    }

    prevPageRef.current = page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page])

  return (
    <Box style={style} sx={sectionCard}>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={traderUsermetaCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderBottom: `1px solid ${adminColors.borderLight}`,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.8rem',
          },
        }}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadCell}>Email</TableCell>
              <TableCell sx={tableHeadCell}>First Name</TableCell>
              <TableCell sx={tableHeadCell}>Last Name</TableCell>
              <TableCell sx={tableHeadCell}>Phone</TableCell>
              <TableCell sx={tableHeadCell}>GCID</TableCell>
              <TableCell sx={tableHeadCell}>GreenChart</TableCell>
              <TableCell sx={tableHeadCell}>GTF</TableCell>
              <TableCell sx={{ ...tableHeadCell, width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {tradersVisible?.map((traderRow: any) => (
              <TraderRow
                key={traderRow.email}
                traderUsermeta={traderRow}
                traderUsermetaSelected={traderUsermetaSelected}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={traderUsermetaCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${adminColors.borderLight}`,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.8rem',
          },
        }}
      />
    </Box>
  )
}
